import {useEffect, useState} from "react";
import {
    Modal,
    Typography,
    Form,
    Input,
    Select,
    Button,
    Space,
    Row, Col, DatePicker,
} from "antd";
import {ActivityCategoryInfo, ActivityMatchInfo, MatchParticipant} from "../../interface";
import { NOCNameMap } from "../../regionData";
import {addMatchActivityParticipant, updateMatchActivityParticipant} from "../../apiBridge";
import dayjs from "dayjs";
import {MessageInstance} from "antd/es/message/interface";
import { EditOutlined } from '@ant-design/icons';
import {
    categoryRequirementMismatches,
    changedCategoriesWithDerivedState,
    generateParticipantDisplayName,
} from './activityParticipant';


const { Title } = Typography;

interface Props {
    isOpen: boolean;
    activity: ActivityMatchInfo;
    categories: ActivityCategoryInfo[];
    participant: MatchParticipant | null;
    controller: React.Dispatch<React.SetStateAction<boolean>>;
    reloadController: React.Dispatch<React.SetStateAction<boolean>>;
    messageController: MessageInstance;
}

const EditParticipantModal: React.FC<Props> =({
    isOpen,
    activity,
    participant,
    categories,
    controller,
    reloadController,
    messageController
}) => {

    const [form] = Form.useForm();

    const [loadingState, setLoadingState] = useState(false);
    const [isDisplayNameEditable, setIsDisplayNameEditable] = useState(false);

    useEffect(() => {
        if (participant && isOpen) {
            const [noc, number] = participant.license_number.split('-');
            form.setFieldsValue({
                display_name: participant.display_name,
                location: participant.location,
                team_name: participant.team_name,
                category_ids: participant.category_ids,
                gender: participant.gender,
                first_name: participant.first_name,
                last_name: participant.last_name,
                noc: participant.noc,
                license_noc: noc,
                license_number: number,
                birth_date: participant.birth_date ? dayjs(participant.birth_date) : null
            });
            setIsDisplayNameEditable(false);
        }
    }, [form, isOpen, participant]);

    const handleNameChange = () => {
        const firstName = form.getFieldValue('first_name');
        const lastName = form.getFieldValue('last_name');
        if (!isDisplayNameEditable && firstName && lastName) {
            const generatedDisplayName = generateParticipantDisplayName(firstName, lastName);
            form.setFieldValue('display_name', generatedDisplayName);
        }
    };

    const handleLocationChange = (value: string) => {
        const noc = NOCNameMap[value] || '';
        form.setFieldValue('license_noc', noc);
        form.setFieldValue('noc', noc);
    };

    const checkCategoriesWithBracket = (oldValue: number[], newValue: number[]): Promise<boolean> => {
        return new Promise((resolve) => {
            const changed = changedCategoriesWithDerivedState(categories, oldValue, newValue);
            if (changed.length === 0) {
                resolve(true);
                return;
            }

            Modal.confirm({
                title: participant
                    ? 'Warning: Category Change'
                    : 'Warning: Category Assignment',
                content: (
                    <div>
                        <p>
                            {participant
                                ? 'Changing these categories clears their schedule, bracket draft, and published matches. Setup is reset if the draw size changes:'
                                : 'Adding this participant to these categories clears their schedule, bracket draft, and published matches. Setup is reset if the draw size changes:'}
                        </p>
                        <ul>
                            {changed.map(({ category, removed }) => (
                                <li key={category.id}>
                                    {category.name}
                                    {removed ? ' (will be removed)' : ' (will be added)'}
                                </li>
                            ))}
                        </ul>
                        <p>Are you sure you want to proceed?</p>
                    </div>
                ),
                okText: 'Confirm',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    };

    const saveChanges = async (values: {
        birth_date: dayjs.Dayjs;
        gender: string;
        category_ids: number[];
        license_noc: string;
        license_number: string;
        team_name: string;
        first_name: string;
        last_name: string;
        location: string;
        display_name: string;
    }) => {
        setLoadingState(true);

        const invalidCategories = categoryRequirementMismatches(
            values.birth_date.format('YYYY-MM-DD'),
            values.gender,
            categories,
            values.category_ids,
        );

        if (invalidCategories.length > 0) {
            Modal.error({
                title: 'Category Requirements Not Met',
                content: (
                    <div>
                        <p>The following categories have requirements that are not met:</p>
                        <ul>
                            {invalidCategories.map((category, index) => (
                                <li key={index}>{category}</li>
                            ))}
                        </ul>
                    </div>
                ),
                okText: 'Close',
                onOk: async () => {
                    setLoadingState(false);
                },
            });
            return;
        }

        const shouldProceed = await checkCategoriesWithBracket(
            participant?.category_ids || [],
            values.category_ids,
        );
        if (!shouldProceed) {
            setLoadingState(false);
            return;
        }
        await proceedWithSave(values);
    };

    const proceedWithSave = async (values: {
        birth_date: dayjs.Dayjs;
        gender: string;
        category_ids: number[];
        license_noc: string;
        license_number: string;
        team_name: string;
        first_name: string;
        last_name: string;
        location: string;
        display_name: string;
    }) => {
        const participantData = {
            team_name: values.team_name,
            first_name: values.first_name,
            last_name: values.last_name,
            birth_date: values.birth_date.format('YYYY-MM-DD'),
            gender: values.gender,
            license_number: `${values.license_noc}-${values.license_number}`,
            location: values.location,
            noc: values.license_noc,
            category_ids: values.category_ids,
            display_name: values.display_name,
        };

        try {
            if (participant) {
                const res = await updateMatchActivityParticipant(activity.id, participant.id, participantData);
                if (res) {
                    const data = await res.json();
                    if (data.error) {
                        messageController.error(data.error);
                    } else {
                        messageController.success("Participant updated successfully");
                        reloadController(true);
                        controller(false);
                    }
                }
            } else {
                const res = await addMatchActivityParticipant(activity.id, participantData);
                if (res) {
                    const data = await res.json();
                    if (data.error) {
                        messageController.error(data.error);
                    } else {
                        messageController.success("Participant added successfully");
                        reloadController(true);
                        controller(false);
                    }
                }
            }
        } catch {
            messageController.error("An error occurred while saving the participant");
        } finally {
            setLoadingState(false);
        }
    };

    return <>
        <Modal
            footer={null}
            maskClosable={false}
            closeIcon={false}
            centered
            open={isOpen}
            width={420}
            afterOpenChange={(open) => {
                if (!open) {
                    form.resetFields();
                }
            }}
        >
            <Title level={4}>
                {participant ? "Edit Participant" : "Adding a Participant"}
            </Title>

            <Form
                name="updateForm"
                layout="vertical"
                onFinish={saveChanges}
                autoComplete="off"
                form={form}
                initialValues={{gender: null}}
            >

                <Row gutter={12}>
                    <Col sm={12}>
                        <Form.Item
                            label="License Number"
                            required
                        >
                            <Input.Group compact>
                                <Form.Item
                                    name="license_noc"
                                    noStyle
                                    rules={[{ required: true, message: 'NOC is required' }]}
                                >
                                    <Input 
                                        style={{ width: '40%' }} 
                                        placeholder="NOC" 
                                        disabled={true}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="license_number"
                                    noStyle
                                    rules={[{ required: true, message: 'Number is required' }]}
                                >
                                    <Input 
                                        style={{ width: '60%' }} 
                                        placeholder="Number" 
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>

                    <Col sm={12}>
                        <Form.Item
                            label="Display Name" 
                            name="display_name"
                            rules={[{ required: true }]}
                        >
                            <Input 
                                disabled={!isDisplayNameEditable}
                                addonAfter={
                                    participant ? (
                                        <Button 
                                            type="text" 
                                            icon={<EditOutlined />} 
                                            onClick={() => setIsDisplayNameEditable(!isDisplayNameEditable)}
                                            style={{ 
                                                padding: '0 4px',
                                                height: '22px',
                                                width: '22px',
                                                minWidth: '22px'
                                            }}
                                        />
                                    ) : null
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col sm={24}>
                        <Form.Item label="Full Name" required={true}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Form.Item name="first_name" noStyle>
                                    <Input 
                                        placeholder="First Name" 
                                        style={{ flex: 1, marginRight: 8 }}
                                        onChange={handleNameChange}
                                    />
                                </Form.Item>
                                <Form.Item name="last_name" noStyle>
                                    <Input 
                                        placeholder="Last Name" 
                                        style={{ flex: 1 }}
                                        onChange={handleNameChange}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>

                    <Col sm={12} xs={24}>
                        <Form.Item name="birth_date" label="Birth Date"  rules={[{ required: true }]}>
                            <DatePicker picker="date" />
                        </Form.Item>
                    </Col>

                    <Col sm={12}>
                        <Form.Item label="Gender" name="gender" rules={[{ required: true }]}>
                            <Select>
                                <Select.Option value="M" key="M">Male</Select.Option>
                                <Select.Option value="F" key="F">Female</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>

                </Row>

                <Form.Item label="Country / Region" name="location" rules={[{ required: true }]}>
                            <Select 
                                onChange={handleLocationChange}
                                showSearch
                                placeholder="Search and select country/region"
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {Object.keys(NOCNameMap).map((region) => (
                                    <Select.Option key={region} value={region}>
                                        {region}
                                    </Select.Option>
                                ))}
                            </Select>
                </Form.Item>

                <Form.Item label="Team/Club" name="team_name" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>


                <Form.Item label="Categories" name="category_ids" rules={[{ required: true }]}>
                    <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Please select"
                        options={categories.map((category) => {
                            return {
                                value: category.id,
                                label: category.name,
                            }
                        })}
                    />
                </Form.Item>

                <div style={{ textAlign: "right" }}>
                    <Space>
                        <Button onClick={() => controller(false)}>Cancel</Button>

                        <Button loading={loadingState} type="primary" htmlType="submit">Save</Button>
                    </Space>
                </div>

            </Form>
        </Modal>
    </>

}

export default EditParticipantModal
